import {
  type ColumnDef,
  type ColumnVisibilityState,
  createColumnHelper,
  type SortingState,
} from "@tanstack/react-table";
import { parseISO } from "date-fns";
import { Columns3, Copy, MoreHorizontal, RotateCw } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { useDisplayLocale } from "~/common/functions/useDisplayLocale";
import { type DataTableFeatures, RemoteDataTable } from "~/components/data-table";
import { EmptyState } from "~/components/empty-state";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui";

import { useRelationList } from "~/features/relations/hooks";
import type {
  RelatedEntityLink,
  RelationListFilters,
  RelationRow,
  RelationSortOrder,
  SortDirection,
} from "~/features/relations/types";
import { toRelationRowList } from "~/features/relations/utils/transformers";

const SORTABLE: ReadonlySet<string> = new Set<RelationSortOrder>(["name", "nameJa", "date", "updatedAt"]);

// Column set mirrors V1 RelationsListTable: date · categories · source · target · updatedAt.
// Relations have no displayed name in the list (unlike entities) — identity is the source→target edge.
const RELATION_COLUMNS = [
  { id: "date", labelKey: "ui.date" },
  { id: "categories", labelKey: "model.category.modelNames" },
  { id: "source", labelKey: "model.record.relation.source" },
  { id: "target", labelKey: "model.record.relation.target" },
  { id: "updatedAt", labelKey: "model.common.updatedAt" },
] as const;

interface RelationsTableProps {
  filters: RelationListFilters;
  sortOrder: RelationSortOrder;
  direction: SortDirection;
  onSortingChange: (order: RelationSortOrder, direction: SortDirection) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  isPending: boolean;
  onSelect: (id: string) => void;
  onAddCategory: (categoryId: string) => void;
}

const helper = createColumnHelper<DataTableFeatures, RelationRow>();

function EntityLinks({ links }: { links: RelatedEntityLink[] }) {
  if (links.length === 0) return <span className="text-fg-3">—</span>;
  return (
    <div className="flex w-[12rem] flex-col gap-0.5">
      {links.map((e) => (
        <span key={e.id} className="truncate text-fg-1" title={e.displayName || e.id}>
          {e.displayName || e.id}
        </span>
      ))}
    </div>
  );
}

export function RelationsTable({
  filters,
  sortOrder,
  direction,
  onSortingChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  isPending,
  onSelect,
  onAddCategory,
}: RelationsTableProps) {
  const { t } = useTranslation();
  const locale = useDisplayLocale();
  const { data, refetch } = useRelationList({ filters, sortOrder, direction, pageIndex, pageSize });
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>({});

  const rows = useMemo(() => toRelationRowList(data.records, locale), [data.records, locale]);
  const totalCount = data.recordsAggregate.aggregate?.totalCount ?? 0;

  const isVisible = (id: string) => columnVisibility[id] !== false;
  const setVisibility = (id: string, next: boolean) => setColumnVisibility((prev) => ({ ...prev, [id]: next }));
  const toggleAll = (next: boolean) =>
    setColumnVisibility(Object.fromEntries(RELATION_COLUMNS.map((c) => [c.id, next])));

  const sorting: SortingState = useMemo(() => [{ id: sortOrder, desc: direction === "desc" }], [sortOrder, direction]);

  const handleSortingChange = (next: SortingState) => {
    const first = next[0];
    if (!first || !SORTABLE.has(first.id)) {
      onSortingChange("updatedAt", "desc");
      return;
    }
    onSortingChange(first.id as RelationSortOrder, first.desc ? "desc" : "asc");
  };

  const handleCopyId = async (id: string) => {
    try {
      await copyToClipboard(id);
      toast.success(t("feature.common.idCopied"));
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };

  const columns: ColumnDef<DataTableFeatures, RelationRow, any>[] = [
    helper.accessor("dateRange", {
      id: "date",
      header: () => t("ui.date"),
      cell: (info) => <span className="whitespace-nowrap tabular-nums text-fg-2">{info.getValue() || "—"}</span>,
    }),
    helper.display({
      id: "categories",
      header: () => t("model.category.modelNames"),
      enableSorting: false,
      cell: (info) => {
        const { categories } = info.row.original;
        if (categories.length === 0) return <span className="text-fg-3">—</span>;
        return (
          <div className="flex w-[10rem] flex-wrap gap-1">
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onAddCategory(c.id)}
                className="rounded-[var(--radius-pill)]"
                aria-label={t("feature.common.filterByCategory", { defaultValue: "Filter by {{name}}", name: c.label })}
              >
                <Badge tone="default" className="cursor-pointer hover:bg-bg-tint">
                  {c.label}
                </Badge>
              </button>
            ))}
          </div>
        );
      },
    }),
    helper.display({
      id: "source",
      header: () => t("model.record.relation.source"),
      enableSorting: false,
      cell: (info) => <EntityLinks links={info.row.original.sources} />,
    }),
    helper.display({
      id: "target",
      header: () => t("model.record.relation.target"),
      enableSorting: false,
      cell: (info) => <EntityLinks links={info.row.original.targets} />,
    }),
    helper.accessor("updatedAt", {
      id: "updatedAt",
      header: () => t("model.common.updatedAt"),
      cell: (info) => {
        const value = info.getValue();
        return (
          <span className="whitespace-nowrap tabular-nums text-fg-2">
            {value ? t("date.long", { date: parseISO(value) }) : "—"}
          </span>
        );
      },
    }),
    helper.display({
      id: "actions",
      header: () => "",
      enableSorting: false,
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex items-center justify-end gap-1.5">
            <Button type="button" variant="outline" size="sm" onClick={() => onSelect(row.id)}>
              {t("ui.view")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="icon" aria-label={t("ui.operations")}>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => handleCopyId(row.id)}>
                  <Copy />
                  <span>{t("ui.copyId")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    }),
  ];

  const isEmpty = rows.length === 0 && !isPending;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="icon" aria-label={t("ui.columns")}>
              <Columns3 />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[200px]">
            <DropdownMenuItem onSelect={() => toggleAll(false)}>{t("ui.hideAll")}</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => toggleAll(true)}>{t("ui.showAll")}</DropdownMenuItem>
            <DropdownMenuSeparator />
            {RELATION_COLUMNS.map((col) => (
              <DropdownMenuCheckboxItem
                key={col.id}
                checked={isVisible(col.id)}
                onCheckedChange={(checked) => setVisibility(col.id, checked === true)}
                onSelect={(e) => e.preventDefault()}
              >
                {t(col.labelKey)}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button type="button" variant="outline" size="icon" onClick={() => void refetch()} aria-label={t("ui.reload")}>
          <RotateCw />
        </Button>
      </div>

      {isEmpty ? (
        <EmptyState title={t("feature.relations.empty.title")} description={t("feature.relations.empty.description")} />
      ) : (
        <RemoteDataTable<RelationRow>
          columns={columns}
          data={rows}
          rowCount={totalCount}
          pageIndex={pageIndex}
          onPageIndexChange={onPageIndexChange}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          isPending={isPending}
          emptyMessage={t("feature.common.noData")}
        />
      )}
    </div>
  );
}
